import { IconDeviceFloppy } from '@tabler/icons-react';
import { useEffect, useMemo } from 'react';
import { childAddEditErrorParser } from 'utils/errorHandlers/child/childAddEditErrorParser';
import { AppForm, AppFormProps, DietPicker } from 'components/organisms';
import { AppFormItem, AppFormItemGroup, AppText } from 'components/atoms';
import { childrenTexts, commonTexts } from 'consts/text';
import {
    AccountingPeriodDatePickerElement,
    AllergensInputElement,
    BirthDateDatePickerElement,
    BirthPlaceInputElement,
    CityInputElement,
    DeclaredStayHoursTimePicker,
    DescriptionInputElement,
    DiseasesInputElement,
    FirstNameInputElement,
    FormNavButtons,
    GenderPickerElement,
    LastNameInputElement,
    PeselInputElement,
    SocialNetworksImageSharingAgreementPicker,
    StreetAddressInputElement,
    ZipCodeInputElement,
} from 'components/molecules';
import { useEditForm } from 'hooks/useEditForm/useEditForm';
import { ChildDetailed, EditChildFormFields, ParentBasicInfo } from 'types';
import { AxiosError } from 'axios';
import staffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { AppBaseSelect } from 'components/atoms/CommonAppComponents';
import { formFields } from 'consts/form/formFields';
import { If } from '../../../atoms/If/If';
import { getFormatedDietEntryString } from './ChildEditForm.helpers';
import { ChildDietsHistory } from './ChildDietsHistory.styled';

type ChildEditFormProps = Pick<AppFormProps<EditChildFormFields>, 'onFinish' | 'isLoading'> & {
    axiosError?: AxiosError | null;
    initialValues: EditChildFormFields | null;
    childData?: ChildDetailed;
};

export const ChildEditForm = ({
    initialValues,
    axiosError,
    isLoading,
    onFinish,
    childData,
}: ChildEditFormProps) => {
    const { form, setFormData, formInitialData } = useEditForm<EditChildFormFields>();

    const childEditError = axiosError ? childAddEditErrorParser(axiosError) : '';

    useEffect(() => {
        if (!initialValues) {
            return;
        }

        setFormData(initialValues);
    }, [initialValues, setFormData]);

    const recipientOptions = useMemo(() => {
        const options = [];
        if (childData?.child) {
            options.push({
                value: childData.child.id,
                label: `Dziecko: ${childData.child.firstName} ${childData.child.lastName}`,
            });

            if (childData.child.parents) {
                childData.child.parents.forEach((parent: ParentBasicInfo) => {
                    options.push({
                        value: parent.id,
                        label: `Rodzic: ${parent.firstName} ${parent.lastName}`,
                    });
                });
            }
        }
        return options;
    }, [childData]);

    return (
        <AppForm
            name="editChild"
            form={form}
            title={childrenTexts.editChildrenButtonLabel}
            desktopTitleLevel={4}
            isFormHeaderAtBottom
            error={childEditError}
            onFinish={onFinish}
            isLoading={isLoading}
            maxWidth={695}
        >
            <AppFormItemGroup title={childrenTexts.childForm.personalData.personalData}>
                <AppFormItem justifyContent="space-between" doubleItem>
                    {/*TODO: change labels to jsons after inputs refactor in forms*/}
                    <FirstNameInputElement />
                    <LastNameInputElement />
                </AppFormItem>
                <AppFormItem justifyContent="space-between" doubleItem>
                    <PeselInputElement />
                    <GenderPickerElement />
                </AppFormItem>
                <AppFormItem justifyContent="space-between" doubleItem>
                    <BirthDateDatePickerElement />
                    <BirthPlaceInputElement />
                </AppFormItem>
            </AppFormItemGroup>
            <AppFormItemGroup title={childrenTexts.childForm.addressData.addressData}>
                {/*TODO: change labels to jsons after inputs refactor in forms*/}
                <StreetAddressInputElement optional hasOptionalText />
                <AppFormItem justifyContent="space-between" doubleItem>
                    <ZipCodeInputElement optional hasOptionalText />
                    <CityInputElement optional hasOptionalText />
                </AppFormItem>
            </AppFormItemGroup>
            <AppFormItemGroup title={childrenTexts.childForm.settlementData.settlementData}>
                <AppFormItem
                    name={formFields.invoiceRecipientId}
                    label={childrenTexts.childForm.settlementData.invoiceRecipient}
                >
                    <AppBaseSelect
                        options={recipientOptions}
                        placeholder={childrenTexts.childForm.settlementData.firstParentStandard}
                        allowClear
                    />
                </AppFormItem>

                <AppFormItem justifyContent="space-between" doubleItem>
                    <AccountingPeriodDatePickerElement mode="start" />
                    <AccountingPeriodDatePickerElement mode="finish" />
                </AppFormItem>
                <AppFormItem>
                    <DescriptionInputElement
                        optional
                        hasOptionalText
                        label={childrenTexts.childForm.settlementData.notes}
                        style={{ flex: 1 }}
                        marginBottom={0}
                    />
                </AppFormItem>
            </AppFormItemGroup>
            {/*TODO: check if institution has settlements per hour*/}
            <AppFormItemGroup title={childrenTexts.childForm.settlementData.declaredStayHours}>
                <AppFormItem justifyContent="space-between" doubleItem>
                    <DeclaredStayHoursTimePicker mode="start" />
                    <DeclaredStayHoursTimePicker mode="finish" />
                </AppFormItem>
            </AppFormItemGroup>
            <If condition={childData?.diet?.entries?.length}>
                <ChildDietsHistory>
                    <AppText textType="BodyMediumSemiBold" margin={0} marginBottom={8}>
                        Historia diet
                    </AppText>
                    {(childData?.diet?.entries || []).map((entry) => {
                        return (
                            <AppText textType="BodyMSRegular" margin={0}>
                                {getFormatedDietEntryString(entry)}
                            </AppText>
                        );
                    })}
                </ChildDietsHistory>
            </If>
            <DietPicker
                httpMethod={{
                    getDiets: staffHTTPService.diets.getDiets,
                    getMealTypes: staffHTTPService.mealTypes.getMealTypes,
                    getChildDiet: staffHTTPService.diets.getChildDiet,
                }}
            />
            <DiseasesInputElement optional hasOptionalText />
            <AllergensInputElement optional hasOptionalText />
            <SocialNetworksImageSharingAgreementPicker />
            <FormNavButtons
                formValuesBeforeChanges={formInitialData}
                buttonText={commonTexts.actionLabels.save}
                leftIcon={<IconDeviceFloppy size={20} />}
            />
        </AppForm>
    );
};
